import dagre from '@dagrejs/dagre';
import { OPTIONS_TO_DISPLAY, EXCLUDE_FOR_SIDEBAR_IPTS } from '../constant';

const NODE_WIDTH = 250;
const OPTION_NODE_WIDTH = 380;  // wider for nodes with options list
const FIXED_GAP = 12;           // px between nodes in the same column

// Height constants matching the redesigned CSS:
//   .node-type-header min-height=28px
//   .callflow-body padding=8px×2=16px
//   .node-badge padding+text ~20px
//   .option padding=10px×2 + text ~16px = 36px
const BADGES_ROW = 22         // one row of badges
const OPTION_ROW = 34         // each option row (padding + text)
const BASE_MIN = 48           // header(28) + body-padding(16) + buffer(4)

/**
 * Calculate whether a node will render the badges row.
 */
function hasBadges(data) {
  if (!data) return false
  if (data.mandatory || data.filterKey) return true
  const exclude = EXCLUDE_FOR_SIDEBAR_IPTS?.[data.type] || []
  if (!exclude.includes('inputMode') && data.inputMode) return true
  if (!exclude.includes('inputRegex') && data.inputRegex) return true
  if (!exclude.includes('minSize') && data.minSize > 0) return true
  if (!exclude.includes('maxSize') && data.maxSize > 0) return true
  return false
}

/**
 * Estimate rendered node height for dagre layout.
 * After the redesign, nodes vary in height depending on options count
 * and badge visibility — using a fixed height causes overlaps.
 */
function getNodeHeight(node) {
  const data = node?.data || {}
  let height = BASE_MIN

  // Options list — each displayed option adds ~OPTION_ROW px
  if (data.answerOptionList?.length) {
    const count = data.answerOptionList.length
    const displayCount = count > OPTIONS_TO_DISPLAY ? OPTIONS_TO_DISPLAY + 1 : count
    height += displayCount * OPTION_ROW
  }

  // Badges row — adds one row of chips
  if (hasBadges(data)) {
    height += BADGES_ROW
  }

  return height
}

/**
 * Auto-layout nodes and edges using dagre.
 * Returns new nodes with computed positions.
 *
 * @param {Array} nodes - ReactFlow nodes
 * @param {Array} edges - ReactFlow edges
 * @param {string} direction - Layout direction ('LR' = left-to-right)
 * @returns {{ nodes: Array, edges: Array }}
 */
export function getLayoutedElements(nodes, edges, direction = 'LR') {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: 90,
    nodesep: FIXED_GAP,
  });

  nodes.forEach((node) => {
    const height = getNodeHeight(node);
    const hasOptions = node?.data?.answerOptionList?.length > 0;
    dagreGraph.setNode(node.id, {
      width: node.measured?.width || (hasOptions ? OPTION_NODE_WIDTH : NODE_WIDTH),
      height: node.measured?.height || height,
    });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  // Convert dagre center → ReactFlow top-left, compute per-node widths
  const laidOut = nodes.map((node) => {
    const d = dagreGraph.node(node.id);
    const hasOptions = node?.data?.answerOptionList?.length > 0;
    const w = node.measured?.width || (hasOptions ? OPTION_NODE_WIDTH : NODE_WIDTH);
    const h = node.measured?.height || getNodeHeight(node);
    return {
      node,
      rank: d.rank || 0,
      width: w,
      height: h,
      // dagre center → top-left
      x: d.x - w / 2,
      y: d.y - h / 2,
    };
  });

  // Group by rank (column) and post-process: top-align + fixed gap
  const rankGroups = new Map();
  laidOut.forEach((item) => {
    if (!rankGroups.has(item.rank)) rankGroups.set(item.rank, []);
    rankGroups.get(item.rank).push(item);
  });

  const GAP = FIXED_GAP;
  for (const group of rankGroups.values()) {
    // Sort top-to-bottom within the column
    group.sort((a, b) => a.y - b.y);

    // Find the minimum x in this rank — align all nodes to that left edge
    const minX = Math.min(...group.map(g => g.x));

    // Stack vertically with fixed gap, starting from the topmost node
    let cursorY = group[0].y;
    for (const item of group) {
      item.x = minX;
      item.y = cursorY;
      cursorY += item.height + GAP;
    }
  }

  const newNodes = laidOut.map(({ node, x, y }) => ({
    ...node,
    position: { x, y },
  }));

  return { nodes: newNodes, edges };
}

/**
 * Convert API questionList into ReactFlow nodes and edges.
 *
 * @param {Array} questionList - Questions from API (with id, type, data, nextQuestionMap)
 * @returns {{ nodes: Array, edges: Array }}
 */
export function initDataFromQuestions(questionList) {
  if (!questionList || questionList.length === 0) {
    return { nodes: [], edges: [] };
  }

  // Deep-clone each item so node mutations don't propagate to Redux/local state
  const initNodes = questionList.map((item) => ({
    id: item?.id,
    type: item?.type,
    data: JSON.parse(JSON.stringify(item)),
  }));

  // Build a set of valid node IDs to prevent edges referencing non-existent nodes
  const nodeIds = new Set(initNodes.map((n) => n.id).filter(Boolean));

  const initEdges = questionList
    .map((item) => ({
      id: item?.id,
      source: item?.id,
      target: item?.nextQuestionMap,
    }))
    .filter((i) => i?.target && nodeIds.has(i?.target));

  return { nodes: initNodes, edges: initEdges };
}
